export const SOURCE_CATEGORIES = [
  { id: "cloud", label: "Cloud Providers" },
  { id: "ai_platform", label: "AI Platforms" },
  { id: "developer_tool", label: "Developer Tools" },
  { id: "manual", label: "Manual Sources" },
];

export const SOURCE_TYPES = [
  {
    id: "gcp-general",
    name: "Google Cloud",
    category: "cloud",
    description: "Billing export, project metadata, Vertex AI usage via service account.",
    authMethod: "service_account_key",
    available: true,
    credentialLabel: "Service Account Key",
    dataTypes: [
      { id: "project_metadata", label: "Project Metadata & Inventory", default: true },
      { id: "vertex_ai_usage", label: "Vertex AI Usage", default: true },
    ],
    fields: [
      { name: "organization_id", label: "Organization ID", type: "text", required: true, placeholder: "123456789012" },
    ],
    icon: { letter: "G", bgColor: "bg-blue-50", textColor: "text-blue-600" },
  },
  {
    id: "gcp-bigquery",
    name: "Google Cloud",
    category: "cloud",
    description: "Billing cost export via BigQuery dataset and service account.",
    authMethod: "service_account_key",
    available: true,
    credentialLabel: "BigQuery Cost Export",
    dataTypes: [
      { id: "billing_export", label: "Billing / Cost Export", default: true },
    ],
    fields: [
      { name: "organization_id", label: "Organization ID", type: "text", required: true, placeholder: "123456789012" },
      { name: "dataset_name", label: "Dataset Name", type: "text", required: true, placeholder: "billing_export" },
      { name: "billing_account_id", label: "Billing Account ID", type: "text", required: true, placeholder: "01ABCD-234EFG-567HIJ" },
    ],
    icon: { letter: "G", bgColor: "bg-blue-50", textColor: "text-blue-600" },
  },
  {
    id: "anthropic-claude",
    name: "Anthropic Claude",
    category: "ai_platform",
    description: "Token usage, cost reports, cached vs uncached input via Admin API.",
    authMethod: "api_key",
    available: true,
    credentialLabel: "Admin API Key",
    dataTypes: [
      { id: "token_usage", label: "Token Usage (input, output, cached tokens per model)", default: true },
      { id: "cost_reports", label: "Cost Reports (spend per model, per workspace)", default: true },
    ],
    fields: [
      { name: "api_key", label: "Admin API Key", type: "password", required: true, placeholder: "sk-ant-admin-...", helpText: "Found in Settings → API Keys." },
      { name: "organization_id", label: "Organization ID", type: "text", required: false, placeholder: "org-...", helpText: "If your key has access to multiple organizations, specify which one to import." },
    ],
    icon: { letter: "A", bgColor: "bg-amber-50", textColor: "text-amber-700" },
  },
  {
    id: "openai-api",
    name: "OpenAI API",
    category: "ai_platform",
    description: "Usage, costs, token breakdown via Admin API key.",
    authMethod: "api_key",
    available: true,
    credentialLabel: "Admin API Key",
    dataTypes: [
      { id: "token_usage", label: "Token Usage (input, output, cached tokens)", default: true },
      { id: "cost_reports", label: "Cost Reports", default: true },
    ],
    fields: [
      { name: "api_key", label: "Admin API Key", type: "password", required: true, placeholder: "sk-admin-...", helpText: "Found in Organization Settings → Admin Keys." },
      { name: "organization_id", label: "Organization ID", type: "text", required: false, placeholder: "org-..." },
    ],
    icon: { letter: "O", bgColor: "bg-emerald-50", textColor: "text-emerald-600" },
  },
  {
    id: "google-gemini",
    name: "Google Gemini",
    category: "ai_platform",
    description: "Token usage metadata via API key. No official cost API.",
    authMethod: "api_key",
    available: true,
    credentialLabel: "API Key",
    dataTypes: [
      { id: "token_usage", label: "Token Usage (prompt tokens, candidate tokens)", default: true },
    ],
    fields: [
      { name: "api_key", label: "API Key", type: "password", required: true, placeholder: "AIza..." },
    ],
    icon: { letter: "Gm", bgColor: "bg-blue-50", textColor: "text-blue-600" },
  },
  {
    id: "cursor",
    name: "Cursor",
    category: "developer_tool",
    description: "Team metrics, model usage, seat data via Admin & Analytics API.",
    authMethod: "api_key",
    available: true,
    credentialLabel: "Access Token",
    dataTypes: [
      { id: "team_seats", label: "Team Members & Seats", default: true },
      { id: "model_usage", label: "Model Usage & Request Analytics", default: true },
    ],
    fields: [
      { name: "api_key", label: "Access Token", type: "password", required: true, placeholder: "csr_...", helpText: "Found in Cursor → Settings → Team → API / Access Tokens. Requires Team Admin role." },
    ],
    icon: { letter: "C", bgColor: "bg-violet-50", textColor: "text-violet-700" },
  },
  {
    id: "csv-upload",
    name: "CSV / Invoice Upload",
    category: "manual",
    description: "Manual upload for sources without API access (ChatGPT, portals).",
    authMethod: "csv_upload",
    available: true,
    credentialLabel: "File Upload",
    dataTypes: [],
    fields: [
      {
        name: "associated_provider",
        label: "Associated Provider",
        type: "select",
        required: true,
        options: [
          { value: "chatgpt", label: "ChatGPT (OpenAI)" },
          { value: "ms365-copilot", label: "MS 365 Copilot" },
          { value: "powerbi-copilot", label: "Copilot (Power BI)" },
          { value: "other", label: "Other" },
        ],
      },
    ],
    icon: { letter: "↑", bgColor: "bg-slate-100", textColor: "text-slate-500" },
  },
  {
    id: "aws",
    name: "AWS",
    category: "cloud",
    description: "Cost Explorer, Bedrock usage, S3 storage via IAM credentials.",
    authMethod: "api_key",
    available: false,
    credentialLabel: "IAM Credentials",
    dataTypes: [],
    fields: [],
    icon: { letter: "A", bgColor: "bg-orange-50", textColor: "text-orange-600" },
  },
  {
    id: "azure",
    name: "Azure",
    category: "cloud",
    description: "Cost Management, Azure OpenAI usage via service principal.",
    authMethod: "api_key",
    available: false,
    credentialLabel: "Service Principal",
    dataTypes: [],
    fields: [],
    icon: { letter: "Az", bgColor: "bg-blue-50", textColor: "text-blue-700" },
  },
  {
    id: "ms365-copilot",
    name: "MS 365 Copilot",
    category: "developer_tool",
    description: "Cost data via M365 Admin Center. No token-level detail.",
    authMethod: "api_key",
    available: false,
    credentialLabel: "Azure AD",
    dataTypes: [],
    fields: [],
    icon: { letter: "M", bgColor: "bg-slate-100", textColor: "text-slate-500" },
  },
];

export const getSourceType = (id) => SOURCE_TYPES.find((s) => s.id === id);

export const getSourcesByCategory = () => {
  return SOURCE_CATEGORIES.map((cat) => ({
    ...cat,
    sources: SOURCE_TYPES.filter((s) => s.category === cat.id),
  })).filter((cat) => cat.sources.length > 0);
};
